// =====================================================================
// src/components/intl-acca/TournamentStateBanner.tsx
// =====================================================================
// Lifecycle-aware top banner for an IntlAccaTournament.
// Renders different copy + dot for each of the four statuses
// (PRE_TOURNAMENT / ACTIVE / POST_TOURNAMENT / ARCHIVED).
//
// Use at the top of every page that has a tournament context.

import type { IntlAccaTournament } from "../../db/intl-acca-types";

interface TournamentStateBannerProps {
  tournament: IntlAccaTournament;
  /** Optional CTA — appears at the right of the banner. */
  action?: { label: string; onClick: () => void };
}

const STATUS_COPY: Record<IntlAccaTournament["status"], string> = {
  PRE_TOURNAMENT: "Pre-tournament · Pass on sale",
  ACTIVE: "Active · Pricing engine live",
  POST_TOURNAMENT: "Post-tournament · Debrief generating",
  ARCHIVED: "Archive · Reference data",
};

function formatDateRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const startStr = new Date(start).toLocaleDateString("en-GB", opts);
  const endStr = new Date(end).toLocaleDateString("en-GB", {
    ...opts,
    year: "numeric",
  });
  return `${startStr} – ${endStr}`;
}

export function TournamentStateBanner({
  tournament,
  action,
}: TournamentStateBannerProps): JSX.Element {
  const t = tournament;
  return (
    <div className={`ia-state-banner ia-state-banner-${t.status}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <span aria-hidden className={`ia-state-dot ia-state-dot-${t.status}`} />
        <div>
          <div
            className="ia-mono"
            style={{
              fontSize: "var(--sz-mono-xs)",
              letterSpacing: "0.22em",
              color: "var(--text-tertiary)",
              textTransform: "uppercase",
            }}
          >
            {t.region} · {formatDateRange(t.start_date, t.end_date)}
          </div>
          <div
            style={{
              fontFamily: "var(--sz-font-head)",
              fontSize: "var(--sz-head-sm)",
              fontWeight: 500,
              marginTop: 4,
              color: "var(--text)",
            }}
          >
            {t.name} <span style={{ color: "var(--text-secondary)" }}>·</span>{" "}
            <span
              className="ia-mono"
              style={{
                fontSize: "var(--sz-mono-md)",
                letterSpacing: "0.16em",
                color: "var(--text-secondary)",
                textTransform: "uppercase",
              }}
            >
              {STATUS_COPY[t.status]}
            </span>
          </div>
        </div>
      </div>
      {action ? (
        <button
          type="button"
          onClick={action.onClick}
          style={{
            background: "transparent",
            color: "var(--text)",
            border: "1px solid var(--border-strong)",
            borderRadius: "var(--sz-radius-md)",
            padding: "8px 14px",
            fontFamily: "var(--sz-font-mono)",
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          {action.label}
        </button>
      ) : null}
    </div>
  );
}
